import type {
  ActionGroupConfig,
  ActionGroupStatus,
} from "@smartsuite/automation-engine-api";

/** The child group lists a container config group nests, in flow order. */
const nestedConfigGroups = (group: ActionGroupConfig): ActionGroupConfig[] => {
  if (group.config.oneofKind === "loop") {
    return group.config.loop.actionGroups;
  }
  if (group.config.oneofKind === "repeat") {
    return group.config.repeat.actionGroups;
  }
  if (group.config.oneofKind === "branches") {
    const { branches, elseBranch } = group.config.branches;
    return [
      ...branches.flatMap((branch) => branch.actionGroups),
      ...(elseBranch !== undefined ? elseBranch.actionGroups : []),
    ];
  }
  return [];
};

/** instanceIds of every group in the config tree, in pre-order. */
const configInstanceIdsInOrder = (groups: ActionGroupConfig[]): number[] =>
  groups.flatMap((group) => [
    group.instanceId,
    ...configInstanceIdsInOrder(nestedConfigGroups(group)),
  ]);

/**
 * Reorders a status group list to the order the automation is CONFIGURED in.
 *
 * A job's `actionGroupsStatus` does not arrive in flow order: the engine
 * persists it as a map keyed by group instanceId (dev job 75a903c8, whose
 * config is `[4, 1]`, stores keys `"4"`, `"1"`), so what reaches the FE is
 * instanceId order -- i.e. the order the groups were CREATED in. That matches
 * the flow only until a group is inserted before an existing one: adding Find
 * records + Create a record (new group 4) above an existing Conditional Logic
 * block (group 1) rendered the block first, ahead of the two actions that run
 * before it (STR-14248). The same holds for the nested lists inside branches
 * and loop iterations, which the engine rebuilds from path-encoded keys.
 *
 * `job.automation.actionGroups` -- the config snapshot taken when the job ran,
 * already the structural source of truth for BranchesGroup/LoopingBlock -- is
 * the only side that carries the real order, so positions come from a
 * pre-order walk of it. Siblings in a status list are always siblings in the
 * config, so one walk of the whole tree orders every level: pass the snapshot's
 * top-level list at any depth.
 *
 * Groups the snapshot does not describe (a job older than the current
 * snapshot, or no snapshot at all) keep their relative order and sort last,
 * so ordering degrades to the incoming order instead of dropping anything.
 */
export const orderStatusGroupsByConfig = (
  groups: ActionGroupStatus[],
  configGroups: ActionGroupConfig[],
): ActionGroupStatus[] => {
  const positions = new Map(
    configInstanceIdsInOrder(configGroups).map((instanceId, index) => [
      instanceId,
      index,
    ]),
  );
  if (positions.size === 0) {
    return groups;
  }

  // MAX_SAFE_INTEGER, not Infinity: two unknown groups must compare equal (a
  // NaN comparator result leaves the sort order unspecified).
  const positionOf = (group: ActionGroupStatus): number =>
    positions.get(group.instanceId) ?? Number.MAX_SAFE_INTEGER;

  return [...groups].sort((a, b) => positionOf(a) - positionOf(b));
};
